// ---------------------------------------------------------------------------
// getValuesForDimension
// ---------------------------------------------------------------------------
// Resolves available value options for a given dimension, using either
// API-sourced filter options or hardcoded static lists.
// Shared between Discovery and Funnel pages.
// ---------------------------------------------------------------------------

import type { ValueOption } from '@/components/v11/funnel/modals/ValuePickerModal';
import { FOLLOWER_TIERS } from '@/components/v11/discovery/constants';
import { GENERATION_OPTIONS } from '@/components/v11/funnel/constants';

const STRING_VALUE_DIMENSIONS = new Set<string>(['emotion']);

// Lookup map for follower tier ranges (includes "medio" alias for "mid" since API returns "Medio")
const FOLLOWER_TIER_RANGES = new Map([
  ...FOLLOWER_TIERS.map(tier => [tier.label.toLowerCase(), tier.range] as const),
  ['medio', '50K-500K'], // API returns "Medio" instead of "Mid"
]);

export function getValuesForDimension(
  dimensionKey: string,
  filterOptions: Record<string, unknown> | undefined
): readonly ValueOption[] {
  // Check API-sourced dimensions first
  const DIMENSION_MAP: Partial<Record<string, string>> = {
    platform: 'platforms',
    gender: 'genders',
    country: 'countries',
    followerTier: 'followerTiers',
    emotion: 'emotions',
    ethnicity: 'ethnicities',
    industry: 'industries',
    lifestyle: 'lifestyles',
    statusClass: 'statusClasses',
    language: 'languages',
    brandCategory: 'brandCategories',
  };

  const filterKey = DIMENSION_MAP[dimensionKey];
  if (filterKey && filterOptions) {
    const options = (filterOptions as Record<string, unknown>)[filterKey];
    if (Array.isArray(options)) {
      const useNameAsValue = STRING_VALUE_DIMENSIONS.has(dimensionKey);
      // Filter out "other" from gender options
      const filteredOptions =
        dimensionKey === 'gender'
          ? options.filter((opt: { name: string }) => opt.name.toLowerCase() !== 'other')
          : options;
      return filteredOptions.map((opt: { id: number | string; name: string }) => ({
        value: useNameAsValue ? opt.name : opt.id,
        // Capitalize first letter for gender labels (API returns lowercase)
        // Add range to follower tier labels
        label:
          dimensionKey === 'gender'
            ? opt.name.charAt(0).toUpperCase() + opt.name.slice(1).toLowerCase()
            : dimensionKey === 'followerTier'
              ? `${opt.name} (${FOLLOWER_TIER_RANGES.get(opt.name.toLowerCase()) ?? ''})`
              : opt.name,
        // Include platform identifier for platform dimension so icons render
        ...(dimensionKey === 'platform' ? { platform: opt.name.toLowerCase() } : {}),
      }));
    }
  }

  // Age/Generation - use generation options instead of raw age intervals
  if (dimensionKey === 'age') {
    return GENERATION_OPTIONS.map(gen => ({
      value: gen.key,
      label: gen.label,
    }));
  }

  // MediaType
  if (dimensionKey === 'mediaType') {
    return [
      { value: 1, label: 'Photo' },
      { value: 2, label: 'Carousel' },
      { value: 3, label: 'Reel' },
      { value: 4, label: 'Animated Gif' },
      { value: 5, label: 'Text' },
      { value: 6, label: 'Slideshow' },
    ];
  }

  // Category
  if (dimensionKey === 'category') {
    return [
      { value: 1, label: 'Game Highlights & Player Performances' },
      { value: 2, label: 'Breaking News & Announcements' },
      { value: 3, label: 'Entertainment & Celebrity Appearances' },
      { value: 4, label: 'Throwbacks & Historical Moments' },
      { value: 5, label: 'Fan Engagement & Memes' },
      { value: 6, label: 'Stat Updates & League Standings' },
      { value: 7, label: 'Merchandise & Promotions' },
      { value: 8, label: 'Inspirational & Motivational Posts' },
      { value: 9, label: 'Event & Game Promotions' },
    ];
  }

  // Boolean dimensions
  if (['verified', 'famous', 'isPerson', 'toxic', 'spam'].includes(dimensionKey)) {
    return [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ];
  }

  // Paid/Organic
  if (dimensionKey === 'paidOrganic') {
    return [
      { value: 'paid', label: 'Paid' },
      { value: 'organic', label: 'Organic' },
    ];
  }

  return [];
}

/**
 * Resolve numeric/string IDs to human-readable labels for a given dimension.
 * Used when loading personas whose stored valueLabels are just stringified IDs.
 */
export function resolveValueLabels(
  dimensionKey: string,
  values: readonly (string | number)[],
  existingLabels: readonly string[],
  filterOptions: Record<string, unknown> | undefined
): readonly string[] {
  const options = getValuesForDimension(dimensionKey, filterOptions);

  if (options.length === 0) {
    // Search dimensions (channel, hashtag, brand, team, item, post) —
    // keep existing labels since resolving requires an API call
    return existingLabels.length > 0 ? existingLabels : values.map(String);
  }

  const lookup = new Map(options.map(opt => [String(opt.value), opt.label]));

  return values.map((v, i) => {
    const resolved = lookup.get(String(v));
    if (resolved) return resolved;
    // Fallback: use existing label if available, otherwise stringify
    return existingLabels[i] ?? String(v);
  });
}
